import {
    Card,
    CardFooter,
    CardHeader,
    CardPreview,
    makeStyles,
    mergeClasses,
    Text,
    tokens,
    Tooltip,
    typographyStyles,
} from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CssSelector } from '../../themes/constants/css-selectors';
import { useStackStyles } from '../../themes/styles/flexbox-styles';
import MetadataList, { ShimmeredMetadataList } from '../common/metadata/metadata-list';
import { MetadataItemViewModel, ShimmeredMetadataItemViewModel } from '../common/metadata/models';
import { CardBanner } from './card-banner';
import { ResourceCardBannerViewModel } from './models';
import SecondaryActionsButton from './secondary-actions-button';

interface ResourceCardProps {
    areSecondaryActionsDisabled?: boolean;
    metadata?: MetadataItemViewModel[];
    name: string;
    productIcon: React.ReactNode;
    previewImageSrc: string;
    projectName: string;
    footerContent: JSX.Element;
    secondaryActions?: JSX.Element[];
    useTranslucentPreviewImage?: boolean;
    displayStateAtTheBottom?: boolean;
    isShimmered?: boolean;
    showFooterWhenShimmered?: boolean;
    shimmeredMetadata?: ShimmeredMetadataItemViewModel[];
    cardBanner?: ResourceCardBannerViewModel;
    secondaryActionsAriaLabel?: string;
}

interface ResourceCardPreviewImageProps {
    name: string;
    previewImageSrc: string;
    productIcon: React.ReactNode;
}

interface ResourceCardNameDisplayProps {
    name: string;
}

interface ResourceCardBannerProps {
    useTranslucentPreviewImage?: boolean;
    cardBanner?: ResourceCardBannerViewModel;
}

const messages = defineMessages({
    previewImageAlt: {
        id: 'ResourceCard_PreviewImage_Alt',
        defaultMessage: 'Preview for "{name}"',
        description: 'Alt text for preview image. {name} should not be localized.',
    },
});

/**
 * Styles
 */

const useResourceCardStyles = makeStyles({
    bannerContainer: {
        position: 'absolute',
        textAlign: 'left',
        width: '100%',
        zIndex: 1,
    },
    previewImage: {
        height: '100%',
        // objectFit: 'cover' downscales image without warping aspect ratio
        objectFit: 'cover',
        opacity: '100%',
        width: '100%',
    },
    root: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '4px',
        // Using hidden overflow will ensure preview image doesn't cover rounded corners
        overflow: 'hidden',
        position: 'relative',
        height: '406px',
        width: '260px',
        maxWidth: '532px',

        [CssSelector.ScreenSizeSmallAndBelow]: {
            width: '100%',
        },
    },
    productIconContainer: {
        bottom: '6px',
        left: '6px',
        position: 'absolute',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '2px',
        height: '32px',
        width: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

const useHeaderStyles = makeStyles({
    root: {
        ...typographyStyles.subtitle2,
        overflow: 'hidden',
        width: '200px', //better to not hard-code this...
        display: 'block',
    },
    description: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
});

const usePreviewStyles = makeStyles({
    root: {
        height: '184px',
        // Explicitly assigning relative positioning so status can overlay it
        position: 'relative',
        width: '100%',
    },
});

const useMetadataListStyles = makeStyles({
    root: {
        gap: '10px',
    },
});

const useFooterStyles = makeStyles({
    root: {
        gap: '15px',
        justifyContent: 'end',
        height: '100%',
    },
});

const useFooterContainerStyles = makeStyles({
    root: {
        flexGrow: 1,
        justifyContent: 'end',
    },
});

/**
 * END Styles
 */

const ResourceCardPreviewImage: React.FC<ResourceCardPreviewImageProps> = React.memo(
    (props: ResourceCardPreviewImageProps) => {
        const { name, previewImageSrc, productIcon } = props;

        // Intl hooks
        const { formatMessage } = useIntl();

        // Style hooks
        const previewStyles = usePreviewStyles();
        const resourceCardStyles = useResourceCardStyles();
        const stackStyles = useStackStyles();

        return (
            <div className={mergeClasses(stackStyles.item, previewStyles.root)}>
                <img
                    alt={formatMessage(messages.previewImageAlt, { name })}
                    className={resourceCardStyles.previewImage}
                    src={previewImageSrc}
                />

                {productIcon && <span className={resourceCardStyles.productIconContainer}>{productIcon}</span>}
            </div>
        );
    }
);

const ResourceCardNameDisplay: React.FC<ResourceCardNameDisplayProps> = React.memo(
    (props: ResourceCardNameDisplayProps) => {
        const { name } = props;

        // Style hooks
        const headerStyles = useHeaderStyles();

        return (
            <Tooltip content={name} relationship="label" positioning="above-start">
                <Text role="heading" aria-level={3} truncate wrap={false} className={headerStyles.root}>
                    {name}
                </Text>
            </Tooltip>
        );
    }
);

export const ResourceCardBanner: React.FC<ResourceCardBannerProps> = React.memo((props: ResourceCardBannerProps) => {
    const { cardBanner } = props;

    const resourceCardStyles = useResourceCardStyles();

    if (cardBanner) {
        return (
            <div className={resourceCardStyles.bannerContainer}>
                <CardBanner cardBanner={cardBanner} />
            </div>
        );
    }

    return <></>;
});

export const ResourceCard: React.FC<ResourceCardProps> = React.memo((props: ResourceCardProps) => {
    const {
        areSecondaryActionsDisabled,
        metadata,
        name,
        productIcon,
        previewImageSrc,
        projectName,
        secondaryActions,
        isShimmered,
        showFooterWhenShimmered,
        shimmeredMetadata,
        footerContent,
    } = props;

    const showSecondaryActionsButton = secondaryActions && secondaryActions.length > 0;

    // Style hooks
    const resourceCardStyles = useResourceCardStyles();
    const stackStyles = useStackStyles();
    const headerStyles = useHeaderStyles();
    const footerStyles = useFooterStyles();
    const footerContainerStyles = useFooterContainerStyles();

    return (
        <Card className={resourceCardStyles.root}>
            <CardPreview>
                {!isShimmered && <ResourceCardBanner {...props} />}
                <ResourceCardPreviewImage name={name} previewImageSrc={previewImageSrc} productIcon={productIcon} />
            </CardPreview>
            <CardHeader
                header={<ResourceCardNameDisplay name={name} />}
                description={<div className={headerStyles.description}>{projectName}</div>}
                action={
                    !isShimmered && showSecondaryActionsButton ? (
                        <SecondaryActionsButton
                            disabled={areSecondaryActionsDisabled}
                            actions={secondaryActions}
                            resourceName={name}
                        />
                    ) : undefined
                }
            />
            {isShimmered ? (
                <ShimmeredMetadataList metadataList={shimmeredMetadata} />
            ) : (
                <MetadataList metadataList={metadata} useContainerStyles={useMetadataListStyles} />
            )}
            <div className={mergeClasses(stackStyles.item, footerContainerStyles.root)}>
                <div className={mergeClasses(stackStyles.root, footerStyles.root)}>
                    {(!isShimmered || showFooterWhenShimmered) && <CardFooter>{footerContent}</CardFooter>}
                </div>
            </div>
        </Card>
    );
});

export default ResourceCard;
